import React, { useState } from "react";

import { Box, CircularProgress, Typography } from "@mui/material";
import {
  DynamicStep,
  InvestmentData,
  InvestmentStep,
  ReadyStep,
  SecondUserSummaryStep,
  WelcomeStep
} from "components/Onboarding/Steps";
import { OnboardingAnswers } from "store/types/user";

import { useOnboardingConfig } from "./use-onboarding-config";
import { useOnboardingSubmit } from "./use-onboarding-hooks";

interface OnboardingState {
  answers: OnboardingAnswers;
  investments: InvestmentData[];
  skipped: boolean;
}

export const OnboardingRoutes: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const { steps, deals, loading, error } = useOnboardingConfig();

  // Determine if this is a second user by checking if deals have is_sample: false
  // If any deal has is_sample: false, it means real investments exist (first user already onboarded)
  const accountHasInvestments = deals.some((deal) => !deal.is_sample);
  const isFirstUser = !accountHasInvestments;

  const [state, setState] = useState<OnboardingState>({
    answers: {},
    investments: [],
    skipped: false
  });

  const { submitOnboarding, isSubmitting } = useOnboardingSubmit();

  const handleNext = () => setCurrentStep((s) => s + 1);
  const handleBack = () => setCurrentStep((s) => s - 1);

  const handleAnswer = (stepType: string, questionIndex: number, value: string | string[]) => {
    setState((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [stepType]: {
          ...prev.answers[stepType],
          [questionIndex]: value
        }
      }
    }));
  };

  const handleFinish = async () => {
    await submitOnboarding(state, accountHasInvestments);
  };

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          bgcolor: "#ffffff"
        }}
      >
        <CircularProgress sx={{ color: "#c09560" }} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          bgcolor: "#ffffff",
          px: 3
        }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#0a3142", mb: 1 }}>
          Failed to load onboarding
        </Typography>
        <Typography sx={{ fontSize: "15px", color: "rgba(10, 49, 66, 0.6)" }}>{error}</Typography>
      </Box>
    );
  }

  // Calculate total steps based on flow
  // First user: Welcome + Dynamic steps + Investment + Ready
  // Second user: Welcome + Dynamic steps + Portfolio Summary
  const totalSteps = isFirstUser ? steps.length + 3 : steps.length + 2;
  const dynamicStepsCount = steps.length;

  // First User Flow
  if (isFirstUser) {
    // Welcome step
    if (currentStep === 1) {
      return <WelcomeStep onNext={handleNext} />;
    }

    // Dynamic steps from API
    const dynamicStepIndex = currentStep - 2;
    if (dynamicStepIndex >= 0 && dynamicStepIndex < dynamicStepsCount) {
      const step = steps[dynamicStepIndex];
      const stepAnswers = state.answers[step.type] || {};

      return (
        <DynamicStep
          step={currentStep}
          totalSteps={totalSteps}
          title={step.title}
          questions={step.questions}
          answers={stepAnswers}
          onAnswer={(qIndex: number, value: string | string[]) => handleAnswer(step.type, qIndex, value)}
          onNext={handleNext}
          onBack={handleBack}
          onSkip={() => {
            // Clear answers for this step when skipped
            setState((prev) => {
              const newAnswers = { ...prev.answers };
              delete newAnswers[step.type];
              return { ...prev, answers: newAnswers };
            });
            handleNext();
          }}
        />
      );
    }

    // Get asset classes from user_interests step for investment creation
    const interestsAnswers = state.answers.user_interests || {};
    const assetClasses: string[] = [];
    const interestsStep = steps.find((s) => s.type === "user_interests");
    if (interestsStep) {
      interestsStep.questions.forEach((_, qIndex: number) => {
        const answer = interestsAnswers[qIndex];
        if (Array.isArray(answer)) {
          assetClasses.push(...answer);
        } else if (answer) {
          assetClasses.push(answer);
        }
      });
    }

    // Investment step
    if (currentStep === dynamicStepsCount + 2) {
      return (
        <InvestmentStep
          step={currentStep}
          totalSteps={totalSteps}
          initialInvestments={state.investments}
          onComplete={(investments) => setState({ ...state, investments, skipped: false })}
          onNext={handleNext}
          onBack={handleBack}
          onSkip={() => {
            setState({ ...state, investments: [], skipped: true });
            handleNext();
          }}
        />
      );
    }

    // Ready step
    if (currentStep === dynamicStepsCount + 3) {
      return (
        <ReadyStep
          step={currentStep}
          totalSteps={totalSteps}
          investments={state.investments}
          skipped={state.skipped}
          sampleDeals={deals}
          submitting={isSubmitting}
          onBack={handleBack}
          onFinish={handleFinish}
          onAddAnother={() => setCurrentStep(dynamicStepsCount + 2)}
        />
      );
    }

    // Fallback
    return <WelcomeStep onNext={handleNext} />;
  }

  // Second User Flow
  // Welcome step
  if (currentStep === 1) {
    return <WelcomeStep onNext={handleNext} />;
  }

  // Dynamic steps from API
  const dynamicStepIndex = currentStep - 2;
  if (dynamicStepIndex >= 0 && dynamicStepIndex < steps.length) {
    const step = steps[dynamicStepIndex];
    const stepAnswers = state.answers[step.type] || {};

    return (
      <DynamicStep
        step={currentStep}
        totalSteps={totalSteps}
        title={step.title}
        questions={step.questions}
        answers={stepAnswers}
        onAnswer={(qIndex: number, value: string | string[]) => handleAnswer(step.type, qIndex, value)}
        onNext={handleNext}
        onBack={handleBack}
        onSkip={() => {
          // Clear answers for this step when skipped
          setState((prev) => {
            const newAnswers = { ...prev.answers };
            delete newAnswers[step.type];
            return { ...prev, answers: newAnswers };
          });
          handleNext();
        }}
      />
    );
  }

  // Portfolio Summary step - show real investments from the account
  if (currentStep === steps.length + 2) {
    const realDeals = deals.filter((deal) => !deal.is_sample);
    return (
      <SecondUserSummaryStep
        step={currentStep}
        totalSteps={totalSteps}
        deals={realDeals}
        submitting={isSubmitting}
        onBack={handleBack}
        onFinish={handleFinish}
      />
    );
  }

  // Fallback
  return <WelcomeStep onNext={handleNext} />;
};
